import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

const SUBSCRIPTION_CACHE_KEY = 'elevadvisor_subscription_cache';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

interface SubscriptionCache {
  subscribed: boolean;
  email: string;
  timestamp: number;
}

interface MemberProfile {
  id: string;
  user_id: string;
  email: string;
  name: string | null;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  stripe_customer_id: string | null;
  subscription_status: string;
  subscription_end_date: string | null;
  tier: 'free' | 'vip' | 'admin' | null;
  onboarding_completed: boolean | null;
  onboarding_role: string | null;
  onboarding_frequency: string | null;
  onboarding_pain_point: string | null;
  monthly_chat_limit: number | null;
  chat_messages_used: number | null;
}

interface MembersAuthContextType {
  user: User | null;
  session: Session | null;
  profile: MemberProfile | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isSubscribed: boolean;
  isCheckingSubscription: boolean;
  isAdmin: boolean;
  tier: 'free' | 'vip' | 'admin';
  onboardingCompleted: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, name?: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshSubscription: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const MembersAuthContext = createContext<MembersAuthContextType | undefined>(undefined);

// Cache helpers
const getSubscriptionCache = (email: string): boolean | null => {
  try {
    const cached = localStorage.getItem(SUBSCRIPTION_CACHE_KEY);
    if (!cached) return null;
    
    const data: SubscriptionCache = JSON.parse(cached);
    
    // Check if cache is for same email and not expired
    if (data.email === email && Date.now() - data.timestamp < CACHE_TTL_MS) {
      console.log('[MembersAuth] Using cached subscription status:', data.subscribed);
      return data.subscribed;
    }
    
    return null;
  } catch {
    return null;
  }
};

const setSubscriptionCache = (email: string, subscribed: boolean) => {
  try {
    const data: SubscriptionCache = {
      subscribed,
      email,
      timestamp: Date.now()
    };
    localStorage.setItem(SUBSCRIPTION_CACHE_KEY, JSON.stringify(data));
    console.log('[MembersAuth] Cached subscription status:', subscribed);
  } catch {
    // Ignore storage errors
  }
};

const clearSubscriptionCache = () => {
  try {
    localStorage.removeItem(SUBSCRIPTION_CACHE_KEY);
  } catch {
    // Ignore storage errors
  }
};

export function MembersAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<MemberProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Check if user is admin or master_admin via SECURITY DEFINER function
  const checkAdminStatus = async (userId: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase.rpc('get_admin_level', { _user_id: userId });

      if (error) {
        console.error('[MembersAuth] Admin check error:', error);
        return false;
      }
      return data === 'admin' || data === 'master_admin';
    } catch (err) {
      console.error('[MembersAuth] Admin check exception:', err);
      return false;
    }
  };

  // Fetch member profile
  const fetchProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('member_profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.error('[MembersAuth] Profile fetch error:', error);
        return null;
      }
      // Map database fields to MemberProfile interface
      if (data) {
        return {
          ...data,
          first_name: (data as Record<string, unknown>).first_name as string | null ?? null,
          last_name: (data as Record<string, unknown>).last_name as string | null ?? null,
          avatar_url: (data as Record<string, unknown>).avatar_url as string | null ?? null,
        } as MemberProfile;
      }
      return null;
    } catch (err) {
      console.error('[MembersAuth] Profile fetch exception:', err);
      return null;
    }
  };

  // Refresh profile data
  const refreshProfile = async () => {
    if (!user) return;
    const profileData = await fetchProfile(user.id);
    if (profileData) {
      setProfile(profileData);
    }
  };

  // Check subscription status via Stripe (with cache)
  const checkSubscription = async (email: string, forceRefresh = false): Promise<{ subscribed: boolean; subscriptionEnd: string | null }> => {
    // Check cache first (unless force refresh)
    if (!forceRefresh) {
      const cached = getSubscriptionCache(email);
      if (cached !== null) {
        return { subscribed: cached, subscriptionEnd: null };
      }
    }

    try {
      console.log('[MembersAuth] Checking subscription with Stripe...');
      const { data, error } = await supabase.functions.invoke('check-members-subscription', {
        body: { email }
      });

      if (error) {
        console.error('[MembersAuth] Subscription check error:', error);
        return { subscribed: false, subscriptionEnd: null };
      }

      const subscribed = data?.subscribed === true;
      const subscriptionEnd = data?.subscription_end || null;
      setSubscriptionCache(email, subscribed);
      return { subscribed, subscriptionEnd };
    } catch (err) {
      console.error('[MembersAuth] Subscription check exception:', err);
      return { subscribed: false, subscriptionEnd: null };
    }
  };

  // Refresh subscription status (force refresh from Stripe)
  const refreshSubscription = async () => {
    if (!user?.email) return;
    
    setIsCheckingSubscription(true);
    try {
      console.log('[MembersAuth] Force refreshing subscription from Stripe...');
      
      const { data, error } = await supabase.functions.invoke('check-members-subscription', {
        body: { email: user.email }
      });

      if (error) {
        console.error('[MembersAuth] Subscription refresh error:', error);
        return;
      }

      const subscribed = data?.subscribed === true;
      const subscriptionEnd = data?.subscription_end || null;
      
      // Update cache
      setSubscriptionCache(user.email, subscribed);
      setIsSubscribed(subscribed);
      
      // Sync to database for caching
      if (profile) {
        const updateData: Record<string, string | null> = {
          subscription_status: subscribed ? 'active' : 'inactive'
        };
        
        if (subscriptionEnd) {
          updateData.subscription_end_date = subscriptionEnd;
        }
        
        const { error: updateError } = await supabase
          .from('member_profiles')
          .update(updateData)
          .eq('user_id', user.id);
          
        if (updateError) {
          console.error('[MembersAuth] Profile sync error:', updateError);
        } else {
          console.log('[MembersAuth] Synced subscription to database:', subscribed);
          // Update local profile state
          setProfile(prev => prev ? {
            ...prev,
            subscription_status: subscribed ? 'active' : 'inactive',
            subscription_end_date: subscriptionEnd || prev.subscription_end_date
          } : null);
        }
      }
    } finally {
      setIsCheckingSubscription(false);
    }
  };

  // Initialize auth state
  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        console.log('[MembersAuth] Auth state changed:', event);
        setSession(newSession);
        setUser(newSession?.user ?? null);

        // Defer profile fetch to avoid deadlock
        if (newSession?.user) {
          setTimeout(async () => {
            const profileData = await fetchProfile(newSession.user.id);
            setProfile(profileData);
            
            // Check admin status
            const adminStatus = await checkAdminStatus(newSession.user.id);
            setIsAdmin(adminStatus);
            
            if (newSession.user.email) {
              const { subscribed, subscriptionEnd } = await checkSubscription(newSession.user.email);
              // Admins are always treated as subscribed
              setIsSubscribed(subscribed || adminStatus);
              
              // Sync subscription_end_date to profile if available
              if (profileData && subscriptionEnd && profileData.subscription_end_date !== subscriptionEnd) {
                await supabase
                  .from('member_profiles')
                  .update({ 
                    subscription_status: subscribed ? 'active' : 'inactive',
                    subscription_end_date: subscriptionEnd 
                  })
                  .eq('user_id', newSession.user.id);
                  
                setProfile(prev => prev ? { ...prev, subscription_end_date: subscriptionEnd } : null);
              }
            }
            setIsLoading(false);
          }, 0);
        } else {
          setProfile(null);
          setIsSubscribed(false);
          setIsAdmin(false);
          setIsLoading(false);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(async ({ data: { session: existingSession } }) => {
      setSession(existingSession);
      setUser(existingSession?.user ?? null);

      if (existingSession?.user) {
        const profileData = await fetchProfile(existingSession.user.id);
        setProfile(profileData);
        
        // Check admin status
        const adminStatus = await checkAdminStatus(existingSession.user.id);
        setIsAdmin(adminStatus);
        
        if (existingSession.user.email) {
          const { subscribed, subscriptionEnd } = await checkSubscription(existingSession.user.email);
          // Admins are always treated as subscribed
          setIsSubscribed(subscribed || adminStatus);
          
          // Sync subscription_end_date to profile if available
          if (profileData && subscriptionEnd && profileData.subscription_end_date !== subscriptionEnd) {
            await supabase
              .from('member_profiles')
              .update({ 
                subscription_status: subscribed ? 'active' : 'inactive',
                subscription_end_date: subscriptionEnd 
              })
              .eq('user_id', existingSession.user.id);
              
            setProfile(prev => prev ? { ...prev, subscription_end_date: subscriptionEnd } : null);
          }
        }
      }
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Sign in
  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) return { error };
      return { error: null };
    } catch (err) {
      return { error: err as Error };
    }
  };

  // Sign up
  const signUp = async (email: string, password: string, name?: string) => {
    try {
      // Redirect to onboarding after email confirmation - new users need to complete onboarding first
      const redirectUrl = `${window.location.origin}/onboarding`;
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: { name }
        }
      });

      if (error) return { error };

      // Create member profile
      if (data.user) {
        const { error: profileError } = await supabase
          .from('member_profiles')
          .insert({
            user_id: data.user.id,
            email,
            name: name || null
          });

        if (profileError) {
          console.error('[MembersAuth] Profile creation error:', profileError);
        }
      }

      return { error: null };
    } catch (err) {
      return { error: err as Error };
    }
  };

  // Sign out
  const signOut = async () => {
    clearSubscriptionCache(); // Clear cache on logout
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
    setIsSubscribed(false);
    setIsAdmin(false);
  };

  // Computed values for easy access
  const isAuthenticated = !!user;
  const tier: 'free' | 'vip' | 'admin' = isAdmin ? 'admin' : isSubscribed ? 'vip' : 'free';
  const onboardingCompleted = profile?.onboarding_completed ?? false;

  return (
    <MembersAuthContext.Provider value={{
      user,
      session,
      profile,
      isLoading,
      isAuthenticated,
      isSubscribed,
      isCheckingSubscription,
      isAdmin,
      tier,
      onboardingCompleted,
      signIn,
      signUp,
      signOut,
      refreshSubscription,
      refreshProfile
    }}>
      {children}
    </MembersAuthContext.Provider>
  );
}

export function useMembersAuth() {
  const context = useContext(MembersAuthContext);
  if (context === undefined) {
    throw new Error('useMembersAuth must be used within a MembersAuthProvider');
  }
  return context;
}
